<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import type { ExpressionKey } from '@/js/consts/language/en';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import Accordion from '@/js/components/Accordion.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import EchoButton from '@/js/components/EchoButton.vue';
    import RetentionSettingFormFields from '@/js/back-office/components/RetentionSettingFormFields.vue';
    import type { RetentionScope } from '@/js/back-office/components/RetentionSettingFormFields.vue';
    import RetentionBaseSettingForm from '@/js/back-office/components/RetentionBaseSettingForm.vue';
    import type { RetentionBaseSettingEntry } from '@/js/back-office/components/RetentionBaseSettingForm.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import {
        useQueryGetBackOfficeTenants,
        useQueryGetRetentionSettings,
        useQueryGetRetentionDataTypes,
        useQueryGetRetentionQueuesAndSkills,
        useMutationUpsertRetentionSetting,
        useMutationDeleteRetentionSetting,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import {
        isRetentionRuleImplemented,
        type RetentionDataType,
        type RetentionSetting,
    } from '@/js/services/api/backOffice/retentionSettingsApi';

    const successDialogStore = useSuccessDialog();
    const confirmationDialogStore = useConfirmationDialog();

    type Mode = 'list' | 'create' | 'edit';

    const selectedTenantId = ref('');
    const tenantSearch = ref<string | null>(null);
    const mode = ref<Mode>('list');
    const editingSettingId = ref<number | null>(null);

    // Form state.
    const dataType = ref<RetentionDataType>('InteractionData');
    const retentionDays = ref<number | null>(null);
    const scope = ref<RetentionScope>('all');
    const queueId = ref<string | null>(null);
    const skillId = ref<string | null>(null);

    const { data: tenantsData } = useQueryGetBackOfficeTenants();
    const { data: settingsData } = useQueryGetRetentionSettings(selectedTenantId);
    const { data: dataTypesData } = useQueryGetRetentionDataTypes();
    const { data: queuesAndSkillsData } = useQueryGetRetentionQueuesAndSkills(selectedTenantId);

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );
    const dataTypeOptions = computed<RetentionDataType[]>(() => dataTypesData.value ?? []);
    const queueOptions = computed(() => queuesAndSkillsData.value?.queues ?? []);
    const skillOptions = computed(() => queuesAndSkillsData.value?.skills ?? []);
    const settings = computed(() => settingsData.value ?? []);

    type RetentionSettingsGroup = {
        dataType: RetentionDataType;
        settings: RetentionSetting[];
        hasBaseSetting: boolean;
    };

    const isBaseSetting = (setting: RetentionSetting) => !setting.queueId && !setting.skillId;

    const hasBaseSettingFor = (type: RetentionDataType) =>
        settings.value.some((s) => s.dataType === type && isBaseSetting(s));

    // Whether another row already holds the base setting for the currently selected data type -
    // excludes the row being edited, so re-saving the base row itself isn't flagged as a clash.
    const hasOtherBaseSettingFor = computed(() =>
        settings.value.some(
            (s) =>
                s.dataType === dataType.value && isBaseSetting(s) && s.id !== editingSettingId.value
        )
    );

    // Which queues/skills already have a setting for the currently selected data type - used to
    // block creating a duplicate instead of silently overwriting the existing one via upsert.
    // Excludes the row currently being edited, so editing its own queue/skill doesn't flag itself.
    const takenQueueIds = computed(
        () =>
            new Set(
                settings.value
                    .filter(
                        (s) =>
                            s.dataType === dataType.value &&
                            s.queueId &&
                            s.id !== editingSettingId.value
                    )
                    .map((s) => s.queueId as string)
            )
    );
    const takenSkillIds = computed(
        () =>
            new Set(
                settings.value
                    .filter(
                        (s) =>
                            s.dataType === dataType.value &&
                            s.skillId &&
                            s.id !== editingSettingId.value
                    )
                    .map((s) => s.skillId as string)
            )
    );

    const groupedSettings = computed<RetentionSettingsGroup[]>(() => {
        const byDataType = new Map<RetentionDataType, RetentionSetting[]>();
        for (const setting of settings.value) {
            const group = byDataType.get(setting.dataType) ?? [];
            group.push(setting);
            byDataType.set(setting.dataType, group);
        }
        return dataTypeOptions.value
            .filter((dt) => byDataType.has(dt))
            .map((dt) => {
                const groupSettings = byDataType.get(dt) ?? [];
                return {
                    dataType: dt,
                    settings: groupSettings,
                    hasBaseSetting: groupSettings.some(isBaseSetting),
                };
            });
    });

    const dataTypeLabelKeys: Record<RetentionDataType, ExpressionKey> = {
        InteractionData: 'back-office-retention-data-type-interaction-data',
        Coaching: 'back-office-retention-data-type-coaching',
        Summary: 'back-office-retention-data-type-summary',
        Transcription: 'back-office-retention-data-type-transcription',
        AudioFile: 'back-office-retention-data-type-audio-file',
        Ani: 'back-office-retention-data-type-ani',
        FreeTextComments: 'back-office-retention-data-type-free-text-comments',
    };
    const dataTypeLabel = (type: RetentionDataType) => __(dataTypeLabelKeys[type]);

    const dataTypeDescriptionKeys: Record<RetentionDataType, ExpressionKey> = {
        InteractionData: 'back-office-retention-data-type-interaction-data-help',
        Coaching: 'back-office-retention-data-type-coaching-help',
        Summary: 'back-office-retention-data-type-summary-help',
        Transcription: 'back-office-retention-data-type-transcription-help',
        AudioFile: 'back-office-retention-data-type-audio-file-help',
        Ani: 'back-office-retention-data-type-ani-help',
        FreeTextComments: 'back-office-retention-data-type-free-text-comments-help',
    };
    // Coaching is left out: it survives its interaction and keeps its own retention time.
    const typesDeletedWithInteraction: RetentionDataType[] = [
        'Summary',
        'Transcription',
        'AudioFile',
        'Ani',
        'FreeTextComments',
    ];
    const dataTypeDescription = (type: RetentionDataType) => {
        const description = __(dataTypeDescriptionKeys[type]);
        if (!isRetentionRuleImplemented(type) || !typesDeletedWithInteraction.includes(type)) {
            return description;
        }
        return `${description} ${__('back-office-retention-bound-by-interaction-data')}`;
    };

    const queueName = (id: string) => queueOptions.value.find((q) => q.id === id)?.name ?? id;
    const skillName = (id: string) => skillOptions.value.find((s) => s.id === id)?.name ?? id;

    const scopeDescription = (setting: RetentionSetting) => {
        if (setting.queueId) {
            return `${__('back-office-retention-scope-queue')}: ${queueName(setting.queueId)}`;
        }
        if (setting.skillId) {
            return `${__('back-office-retention-scope-skill')}: ${skillName(setting.skillId)}`;
        }
        return __('back-office-retention-scope-all');
    };

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
    };
    const onTenantClear = () => {
        selectedTenantId.value = '';
    };

    watch(selectedTenantId, () => {
        cancelForm();
    });

    const isCreatingBaseSetting = computed(() => !hasBaseSettingFor(dataType.value));

    const clearForm = () => {
        dataType.value = dataTypeOptions.value[0] ?? 'InteractionData';
        retentionDays.value = null;
        scope.value = 'all';
        queueId.value = null;
        skillId.value = null;
    };

    const startCreate = () => {
        clearForm();
        editingSettingId.value = null;
        mode.value = 'create';
    };

    const startEdit = (setting: RetentionSetting) => {
        editingSettingId.value = setting.id;
        dataType.value = setting.dataType;
        retentionDays.value = setting.retentionDays;
        scope.value = setting.queueId ? 'queue' : setting.skillId ? 'skill' : 'all';
        queueId.value = setting.queueId;
        skillId.value = setting.skillId;
        mode.value = 'edit';
    };

    const cancelForm = () => {
        clearForm();
        editingSettingId.value = null;
        mode.value = 'list';
    };

    const { mutateAsync: upsertMutation, isPending: isSaving } =
        useMutationUpsertRetentionSetting();
    const { mutateAsync: deleteMutation } = useMutationDeleteRetentionSetting();

    const deletingSettingId = ref<number | null>(null);

    const submit = async () => {
        if (retentionDays.value === null) return;

        await upsertMutation({
            tenantId: selectedTenantId.value,
            data: {
                id: editingSettingId.value,
                dataType: dataType.value,
                retentionDays: retentionDays.value,
                queueId: scope.value === 'queue' ? queueId.value : null,
                skillId: scope.value === 'skill' ? skillId.value : null,
            },
        });

        successDialogStore.openDialog({ message: __('saved') });
        cancelForm();
    };

    const isSavingBaseSetting = ref(false);

    const submitBaseSetting = async (entries: RetentionBaseSettingEntry[]) => {
        const tenantId = selectedTenantId.value;
        isSavingBaseSetting.value = true;
        try {
            await Promise.all(
                entries.map((entry) => upsertMutation({ tenantId, data: { id: null, ...entry } }))
            );
            successDialogStore.openDialog({ message: __('saved') });
            cancelForm();
        } finally {
            isSavingBaseSetting.value = false;
        }
    };

    const deleteSetting = async (setting: RetentionSetting) => {
        const tenantId = selectedTenantId.value;
        const confirmed = await confirmationDialogStore.openDialog({
            title: __('delete'),
            message: __('back-office-retention-delete-confirm', {
                dataType: dataTypeLabel(setting.dataType),
            }),
        });
        if (!confirmed) return;

        deletingSettingId.value = setting.id;
        try {
            await deleteMutation({ tenantId, id: setting.id });
            if (editingSettingId.value === setting.id) cancelForm();
        } finally {
            deletingSettingId.value = null;
        }
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-retention-settings') }}</h1>
        </template>
        <template #content>
            <EchoInfoBox class="mb-6 max-w-2xl">
                {{ __('back-office-retention-info') }}
            </EchoInfoBox>

            <div class="w-lg max-w-full flex flex-col gap-6">
                <AutoComplete
                    v-model="tenantSearch"
                    input-id="retention-settings-tenant"
                    :label="__('back-office-select-customer')"
                    :options="tenantOptions"
                    @select="onTenantSelect"
                    @clear="onTenantClear"
                />

                <template v-if="selectedTenantId">
                    <div class="flex flex-col gap-3">
                        <div
                            v-if="mode === 'create' && isCreatingBaseSetting"
                            class="rounded-md border border-gray-light p-4"
                        >
                            <RetentionBaseSettingForm
                                v-model:data-type="dataType"
                                :data-type-options="dataTypeOptions"
                                :data-type-label="dataTypeLabel"
                                :data-type-description="dataTypeDescription"
                                :queue-options="queueOptions"
                                :skill-options="skillOptions"
                                :taken-queue-ids="takenQueueIds"
                                :taken-skill-ids="takenSkillIds"
                                :has-existing-base="hasOtherBaseSettingFor"
                                :is-pending="isSavingBaseSetting"
                                @submit="submitBaseSetting"
                                @cancel="cancelForm"
                            />
                        </div>
                        <div
                            v-else-if="mode === 'create'"
                            class="rounded-md border border-gray-light p-4"
                        >
                            <RetentionSettingFormFields
                                v-model:data-type="dataType"
                                v-model:retention-days="retentionDays"
                                v-model:scope="scope"
                                v-model:queue-id="queueId"
                                v-model:skill-id="skillId"
                                id-prefix="retention-create"
                                :data-type-options="dataTypeOptions"
                                :data-type-label="dataTypeLabel"
                                :data-type-description="dataTypeDescription"
                                :queue-options="queueOptions"
                                :skill-options="skillOptions"
                                :taken-queue-ids="takenQueueIds"
                                :taken-skill-ids="takenSkillIds"
                                :has-existing-base="hasOtherBaseSettingFor"
                                :is-pending="isSaving"
                                :show-delete="false"
                                :is-deleting="false"
                                @submit="submit"
                                @cancel="cancelForm"
                            />
                        </div>
                        <div v-else>
                            <EchoButton type="button" variant="primary" @click="startCreate">
                                {{ __('back-office-retention-add') }}
                            </EchoButton>
                        </div>

                        <div
                            v-for="group in groupedSettings"
                            :key="group.dataType"
                            class="rounded-md border border-gray-light"
                            :class="{ 'opacity-50': !isRetentionRuleImplemented(group.dataType) }"
                        >
                            <Accordion>
                                <template #summary>
                                    <span class="flex-1 text-left">
                                        <span class="text-overline">
                                            {{ dataTypeLabel(group.dataType) }}
                                        </span>
                                        <span
                                            v-if="!isRetentionRuleImplemented(group.dataType)"
                                            class="ml-3 text-body text-gray-medium"
                                        >
                                            {{ __('back-office-retention-not-implemented') }}
                                        </span>
                                        <span class="ml-3 text-body text-gray-medium">
                                            ({{ group.settings.length }})
                                        </span>
                                        <span
                                            v-if="!group.hasBaseSetting"
                                            class="ml-3 text-body text-error"
                                        >
                                            {{ __('back-office-retention-no-base-warning') }}
                                        </span>
                                    </span>
                                </template>
                                <div class="flex flex-col px-4 pb-3">
                                    <div
                                        v-for="setting in group.settings"
                                        :key="setting.id"
                                        class="py-3 border-t border-gray-lightest first:border-t-0"
                                    >
                                        <RetentionSettingFormFields
                                            v-if="editingSettingId === setting.id"
                                            v-model:data-type="dataType"
                                            v-model:retention-days="retentionDays"
                                            v-model:scope="scope"
                                            v-model:queue-id="queueId"
                                            v-model:skill-id="skillId"
                                            id-prefix="retention-edit"
                                            :data-type-options="dataTypeOptions"
                                            :data-type-label="dataTypeLabel"
                                            :data-type-description="dataTypeDescription"
                                            :queue-options="queueOptions"
                                            :skill-options="skillOptions"
                                            :taken-queue-ids="takenQueueIds"
                                            :taken-skill-ids="takenSkillIds"
                                            :has-existing-base="hasOtherBaseSettingFor"
                                            :is-pending="isSaving"
                                            :show-delete="true"
                                            :is-deleting="deletingSettingId === setting.id"
                                            @submit="submit"
                                            @cancel="cancelForm"
                                            @delete="deleteSetting(setting)"
                                        />
                                        <div v-else class="flex items-center justify-between">
                                            <p class="text-body">
                                                {{ scopeDescription(setting) }} &middot;
                                                {{
                                                    __('back-office-retention-days-value', {
                                                        days: String(setting.retentionDays),
                                                    })
                                                }}
                                            </p>
                                            <div class="flex gap-2">
                                                <EchoButton
                                                    type="button"
                                                    variant="secondary"
                                                    @click="startEdit(setting)"
                                                >
                                                    {{ __('edit') }}
                                                </EchoButton>
                                                <EchoButton
                                                    type="button"
                                                    variant="secondary"
                                                    :loading="deletingSettingId === setting.id"
                                                    @click="deleteSetting(setting)"
                                                >
                                                    {{ __('delete') }}
                                                </EchoButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Accordion>
                        </div>

                        <p
                            v-if="settingsData && settings.length === 0"
                            class="text-body text-gray-medium"
                        >
                            {{ __('back-office-retention-none') }}
                        </p>
                    </div>
                </template>
            </div>
        </template>
    </SettingsPageLayout>
</template>
